// The message a PLAIN CHAT sends when the user takes Loom's proposal.
//
// Focus Chat (and /chat/:id) show the `draft` of each open story field and each
// open list under Loom's reply, with "Use this text" / "Use this list". A click
// is the user's own answer, sent as an ORDINARY chat message — never a
// `question_answer` (which needs an issue and would adopt the chat for it) and
// never with a `flow` (a bound turn in a plain chat is a flow boundary: it drops
// the agent's whole conversation and flips the chat into refine mode).
//
// A chat message carries no attribution, so the text itself has to say which
// field each answer is for. Each story answer goes out under its NAME LINE
// (`STORY_ANSWER_LABELS`, shared with the chat directive, which tells the model
// the value is only the text below that line); a list answer is already
// self-labelled ("Acceptance criteria:\n- …"). Several answers go out as ONE
// message, blank-line separated, in the card's field order — one message per
// click would kill the agent's running turn once per click.

import {
  isStoryAnswerField,
  listHeadingForField,
  storyAnswerNameLine,
} from "@loom/shared/readiness";

import { FIELD_ORDER } from "../../focus/copy";

import { isListField } from "./loomAnswer";

export interface ChatAnswer {
  /** The readiness field it answers. */
  readonly field: string;
  /** The exact bytes that were on screen (a list: its `buildListAnswer` text). */
  readonly text: string;
}

/** One answer as it goes out: a story field under its name line, anything else
 *  (a self-labelled list) as it is. */
export const chatAnswerBlock = (field: string, text: string): string =>
  isStoryAnswerField(field) ? `${storyAnswerNameLine(field)}\n${text}` : text;

/** The line that names this field in a chat message — `"So that:"`,
 *  `"Acceptance criteria:"` — or null for a field the chat does not name. */
export const chatNameLineOf = (field: string): string | null => {
  if (isStoryAnswerField(field)) return storyAnswerNameLine(field);
  if (isListField(field)) return `${listHeadingForField(field)}:`;
  return null;
};

const orderOf = (field: string): number => {
  const i = FIELD_ORDER.indexOf(field);
  return i === -1 ? FIELD_ORDER.length : i;
};

/**
 * The ONE message for a batch of taken answers plus, last, whatever the user
 * typed into the composer. Blocks in the card's field order, joined by a blank
 * line. A single answer still carries its name line: in a chat nothing else says
 * which field it is for.
 */
export const composeChatBatch = (
  answers: Iterable<ChatAnswer>,
  trailing?: string,
): string => {
  const blocks = [...answers]
    .filter((a) => a.text.trim() !== "")
    .map((a, i) => ({ a, i }))
    .sort((x, y) => orderOf(x.a.field) - orderOf(y.a.field) || x.i - y.i)
    .map(({ a }) => chatAnswerBlock(a.field, a.text));
  if (trailing !== undefined && trailing.trim() !== "") blocks.push(trailing.trim());
  return blocks.join("\n\n");
};

/**
 * Is the user already writing this field in the composer — does a line of it
 * read exactly as the field's name line? Worked out from the text itself, so
 * deleting the line brings the row's buttons back; and while it is there the row
 * offers no accept, so the model never gets two "So that:" blocks.
 */
export const composerNamesField = (composer: string, field: string): boolean => {
  const line = chatNameLineOf(field);
  if (line === null) return false;
  return composer.split("\n").some((l) => l.trim() === line);
};

/** Put a block at the end of the composer text, a blank line after what is
 *  already there. */
export const appendToComposer = (composer: string, block: string): string => {
  if (composer.trim() === "") return block;
  const sep = composer.endsWith("\n\n") ? "" : composer.endsWith("\n") ? "\n" : "\n\n";
  return `${composer}${sep}${block}`;
};
